import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Select, message } from "antd";

import LoadingOverlay from "components/loading-overlay/loading-overlay";

import { AiProvider } from "constants/field-values";
import { AI_PROVIDER_LABELS, CLAUDE_EFFORT_LABELS, DEFAULT_CLAUDE_EFFORT } from "constants/constants";
import { useGetAiModelOptionsQuery } from "graphql/queries/get-ai-model-options.generated";
import { useUpdateAiModelConfigMutation } from "graphql/mutations/update-ai-model-config.generated";

type AiModelOptions = NonNullable<ReturnType<typeof useGetAiModelOptionsQuery>["data"]>["get_ai_model_options"];
type AiFeatureConfig = AiModelOptions["features"][number];

interface AiFeatureCardProps {
	config: AiFeatureConfig;
	providers: AiModelOptions["providers"];
	efforts: string[];
	onSaved: () => void;
}

// The model and provider are saved together, so the card keeps a draft of both and only writes
// when the admin presses Save.
const AiFeatureCard: React.FC<AiFeatureCardProps> = ({ config, providers, efforts, onSaved }) => {
	const [provider, setProvider] = useState(config.provider);
	const [modelName, setModelName] = useState(config.model_name);
	const [effort, setEffort] = useState(config.effort ?? DEFAULT_CLAUDE_EFFORT);
	const [updateAiModelConfig, { loading: isSaving }] = useUpdateAiModelConfigMutation();

	// What the server holds replaces the draft, so after a save the card shows the saved values.
	useEffect(() => {
		setProvider(config.provider);
		setModelName(config.model_name);
		setEffort(config.effort ?? DEFAULT_CLAUDE_EFFORT);
	}, [config.provider, config.model_name, config.effort]);

	const isClaude = provider === AiProvider.Claude;
	const models = providers.find(option => option.provider === provider)?.models ?? [];

	const hasChanges =
		provider !== config.provider ||
		modelName !== config.model_name ||
		(isClaude && effort !== (config.effort ?? DEFAULT_CLAUDE_EFFORT));

	const handleProviderChange = useCallback(
		(nextProvider: string) => {
			setProvider(nextProvider);
			// Going back to the provider already in use keeps its model; any other starts at the top of its list.
			const nextModels = providers.find(option => option.provider === nextProvider)?.models ?? [];
			setModelName(nextProvider === config.provider ? config.model_name : (nextModels[0]?.id ?? ""));
		},
		[providers, config.provider, config.model_name]
	);

	const handleSave = useCallback(async () => {
		try {
			await updateAiModelConfig({
				variables: {
					feature: config.feature,
					provider,
					model_name: modelName,
					effort: isClaude ? effort : null
				}
			});
			message.success(
				`${config.label} now runs on ${models.find(model => model.id === modelName)?.label ?? modelName}.`
			);
			onSaved();
		} catch (error) {
			message.error(error instanceof Error ? error.message : "We couldn't save the model. Please try again.");
		}
	}, [updateAiModelConfig, config.feature, config.label, provider, modelName, isClaude, effort, models, onSaved]);

	return (
		<section className="aim-card">
			<div className="aim-card-header">
				<h2 className="aim-card-title">{config.label}</h2>
				<span className="aim-card-current">
					Running on {AI_PROVIDER_LABELS[config.provider as AiProvider] ?? config.provider} · {config.model_name}
				</span>
			</div>

			<div className="aim-fields">
				<label className="aim-field">
					<span className="aim-field-label">Provider</span>
					<Select
						value={provider}
						onChange={handleProviderChange}
						options={providers.map(option => ({ value: option.provider, label: option.label }))}
					/>
				</label>

				<label className="aim-field">
					<span className="aim-field-label">Model</span>
					<Select
						value={modelName}
						onChange={setModelName}
						options={models.map(model => ({ value: model.id, label: model.label }))}
					/>
				</label>

				{isClaude && (
					<label className="aim-field">
						<span className="aim-field-label">Reasoning effort</span>
						<Select
							value={effort}
							onChange={setEffort}
							options={efforts.map(level => ({ value: level, label: CLAUDE_EFFORT_LABELS[level] ?? level }))}
						/>
					</label>
				)}
			</div>

			{isClaude && (
				<p className="aim-hint">
					Higher effort reads the package more carefully, but takes longer and costs more. High is a good starting
					point; Max can spend several times the tokens for little gain.
				</p>
			)}

			<div className="aim-actions">
				<Button type="primary" onClick={handleSave} loading={isSaving} disabled={!hasChanges || !modelName}>
					Save
				</Button>
			</div>
		</section>
	);
};

/**
 * Lets an admin choose which AI vendor and model runs a feature, for the admin's own workspace.
 *
 * The choice applies to the next run: an analysis already in progress finishes on the model it started with.
 */
const AdminAiModels: React.FC = () => {
	const { data, loading, error, refetch } = useGetAiModelOptionsQuery({
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only"
	});

	const options = data?.get_ai_model_options;

	const handleSaved = useCallback(() => {
		refetch();
	}, [refetch]);

	const cards = useMemo(
		() =>
			(options?.features ?? []).map(feature => (
				<AiFeatureCard
					key={feature.feature}
					config={feature}
					providers={options?.providers ?? []}
					efforts={options?.efforts ?? []}
					onSaved={handleSaved}
				/>
			)),
		[options, handleSaved]
	);

	return (
		<div className="aim-page">
			<div className="aim-shell">
				<h1 className="aim-title">AI Models</h1>
				<p className="aim-subtitle">
					Choose which AI runs each feature. The change applies to this workspace and takes effect on the next run.
				</p>

				{loading && !options && <LoadingOverlay />}
				{error && <p className="aim-error">We couldn't load the model settings. Please refresh the page.</p>}
				{cards}
			</div>
		</div>
	);
};

export default AdminAiModels;
